import { DecisionReason } from "../../../../model/model"
import EvaluationFlow from "../../../flow/EvaluationFlow"
import ExperimentTargetDeterminer from "../match/ExperimentTargetDeterminer"
import ExperimentActionResolver from "../match/ExperimentActionResolver"
import ExperimentTargetRuleDeterminer from "../match/ExperimentTargetRuleDeterminer"
import ExperimentOverrideResolver from "../match/ExperimentOverrideResolver"
import ExperimentContainerResolver from "../match/ExperimentContainerResolver"
import ExperimentLocalEvaluateRequest from "../../../service/experiment/mode/local/ExperimentLocalEvaluateRequest"
import { EvaluatorContext } from "../../../evaluator/Evaluator"
import { ExperimentEvaluateResult } from "../ExperimentEvaluateResult"
import FlowEvaluator from "../../../flow/FlowEvaluator"

export type ExperimentFlow = EvaluationFlow<ExperimentLocalEvaluateRequest, ExperimentEvaluateResult>

export default interface ExperimentLocalFlowEvaluator extends FlowEvaluator<ExperimentLocalEvaluateRequest, ExperimentEvaluateResult> {
  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined
}

export class OverrideEvaluator implements ExperimentLocalFlowEvaluator {
  private readonly overrideResolver: ExperimentOverrideResolver

  constructor(overrideResolver: ExperimentOverrideResolver) {
    this.overrideResolver = overrideResolver
  }

  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    const overriddenVariation = this.overrideResolver.resolveOrNull(request, context)
    if (overriddenVariation) {
      switch (request.experiment.type) {
        case "AB_TEST":
          return ExperimentEvaluateResult.of(DecisionReason.OVERRIDDEN, overriddenVariation)
        case "FEATURE_FLAG":
          return ExperimentEvaluateResult.of(DecisionReason.INDIVIDUAL_TARGET_MATCH, overriddenVariation)
      }
    } else {
      return nextFlow.evaluate(request, context)
    }
  }
}

export class DraftEvaluator implements ExperimentLocalFlowEvaluator {
  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    if (request.experiment.status === "DRAFT") {
      return ExperimentEvaluateResult.ofControl(DecisionReason.EXPERIMENT_DRAFT, request)
    } else {
      return nextFlow.evaluate(request, context)
    }
  }
}

export class PausedEvaluator implements ExperimentLocalFlowEvaluator {
  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    if (request.experiment.status === "PAUSED") {
      switch (request.experiment.type) {
        case "AB_TEST":
          return ExperimentEvaluateResult.ofControl(DecisionReason.EXPERIMENT_PAUSED, request)
        case "FEATURE_FLAG":
          return ExperimentEvaluateResult.ofControl(DecisionReason.FEATURE_FLAG_INACTIVE, request)
      }
    } else {
      return nextFlow.evaluate(request, context)
    }
  }
}

export class CompletedEvaluator implements ExperimentLocalFlowEvaluator {
  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    if (request.experiment.status === "COMPLETED") {
      const winnerVariation = request.experiment._winnerVariationOrNull()
      if (!winnerVariation) {
        throw new Error(`winner variation [${request.experiment.id}]`)
      }
      return ExperimentEvaluateResult.of(DecisionReason.EXPERIMENT_COMPLETED, winnerVariation)
    } else {
      return nextFlow.evaluate(request, context)
    }
  }
}

export class ExperimentTargetEvaluator implements ExperimentLocalFlowEvaluator {
  private readonly experimentTargetDeterminer: ExperimentTargetDeterminer

  constructor(experimentTargetDeterminer: ExperimentTargetDeterminer) {
    this.experimentTargetDeterminer = experimentTargetDeterminer
  }

  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    if (request.experiment.type !== "AB_TEST") {
      throw new Error(`experiment type must be AB_TEST [${request.experiment.id}]`)
    }

    const isUserInExperimentTarget = this.experimentTargetDeterminer.isUserInExperimentTarget(request, context)
    if (isUserInExperimentTarget) {
      return nextFlow.evaluate(request, context)
    } else {
      return ExperimentEvaluateResult.ofControl(DecisionReason.NOT_IN_EXPERIMENT_TARGET, request)
    }
  }
}

export class TrafficAllocateEvaluator implements ExperimentLocalFlowEvaluator {
  private readonly actionResolver: ExperimentActionResolver

  constructor(actionResolver: ExperimentActionResolver) {
    this.actionResolver = actionResolver
  }

  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    if (request.experiment.status !== "RUNNING") {
      throw new Error(`experiment status must be RUNNING [${request.experiment.id}]`)
    }

    if (request.experiment.type !== "AB_TEST") {
      throw new Error(`experiment type must be AB_TEST [${request.experiment.id}]`)
    }

    const variation = this.actionResolver.resolveOrNull(request, context, request.experiment.defaultRule)
    if (!variation) {
      return ExperimentEvaluateResult.ofControl(DecisionReason.TRAFFIC_NOT_ALLOCATED, request)
    }

    if (variation.isDropped) {
      return ExperimentEvaluateResult.ofControl(DecisionReason.VARIATION_DROPPED, request)
    }

    return ExperimentEvaluateResult.of(DecisionReason.TRAFFIC_ALLOCATED, variation)
  }
}

export class TargetRuleEvaluator implements ExperimentLocalFlowEvaluator {
  private readonly targetRuleDeterminer: ExperimentTargetRuleDeterminer
  private readonly actionResolver: ExperimentActionResolver

  constructor(targetRuleDeterminer: ExperimentTargetRuleDeterminer, actionResolver: ExperimentActionResolver) {
    this.targetRuleDeterminer = targetRuleDeterminer
    this.actionResolver = actionResolver
  }

  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    if (request.experiment.status !== "RUNNING") {
      throw new Error(`experiment status must be RUNNING [${request.experiment.id}]`)
    }

    if (request.experiment.type !== "FEATURE_FLAG") {
      throw new Error(`experiment type must be FEATURE_FLAG [${request.experiment.id}]`)
    }

    if (!request.user.identifiers[request.experiment.identifierType]) {
      return nextFlow.evaluate(request, context)
    }

    const targetRule = this.targetRuleDeterminer.determineTargetRuleOrNull(request, context)

    if (!targetRule) {
      return nextFlow.evaluate(request, context)
    }

    const variation = this.actionResolver.resolveOrNull(request, context, targetRule.action)
    if (!variation) {
      throw new Error(`FeatureFlag must decide the Variation [${request.experiment.id}]`)
    }
    return ExperimentEvaluateResult.of(DecisionReason.TARGET_RULE_MATCH, variation)
  }
}

export class DefaultRuleEvaluator implements ExperimentLocalFlowEvaluator {
  private readonly actionResolver: ExperimentActionResolver

  constructor(actionResolver: ExperimentActionResolver) {
    this.actionResolver = actionResolver
  }

  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    if (request.experiment.status !== "RUNNING") {
      throw new Error(`experiment status must be RUNNING [${request.experiment.id}]`)
    }

    if (request.experiment.type !== "FEATURE_FLAG") {
      throw new Error(`experiment type must be FEATURE_FLAG [${request.experiment.id}]`)
    }

    if (!request.user.identifiers[request.experiment.identifierType]) {
      return ExperimentEvaluateResult.ofControl(DecisionReason.DEFAULT_RULE, request)
    }

    const variation = this.actionResolver.resolveOrNull(request, context, request.experiment.defaultRule)
    if (!variation) {
      throw new Error(`FeatureFlag must decide the Variation [${request.experiment.id}]`)
    }

    return ExperimentEvaluateResult.of(DecisionReason.DEFAULT_RULE, variation)
  }
}

export class ContainerEvaluator implements ExperimentLocalFlowEvaluator {
  private readonly containerResolver: ExperimentContainerResolver

  constructor(containerResolver: ExperimentContainerResolver) {
    this.containerResolver = containerResolver
  }

  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    const containerId = request.experiment.containerId
    if (!containerId) {
      return nextFlow.evaluate(request, context)
    }

    const container = request.workspace.getContainerOrNull(containerId)
    if (!container) {
      throw new Error(`container[${containerId}]`)
    }

    const isUserInContainerGroup = this.containerResolver.isUserInContainerGroup(request, context, container)
    if (isUserInContainerGroup) {
      return nextFlow.evaluate(request, context)
    } else {
      return ExperimentEvaluateResult.ofControl(DecisionReason.NOT_IN_MUTUAL_EXCLUSION_EXPERIMENT, request)
    }
  }
}

export class IdentifierEvaluator implements ExperimentLocalFlowEvaluator {
  evaluate(
    request: ExperimentLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: ExperimentFlow
  ): ExperimentEvaluateResult | undefined {
    if (request.user.identifiers[request.experiment.identifierType]) {
      return nextFlow.evaluate(request, context)
    } else {
      return ExperimentEvaluateResult.ofControl(DecisionReason.IDENTIFIER_NOT_FOUND, request)
    }
  }
}
